/**
 * `MoveHoldBanner` — the **mover waiting-state** for a checkpoint decision. A `request_move` that
 * a checkpoint consumer halts routes its prompt to the WATCHER + GM, never to the mover, so the
 * mover would otherwise be left staring at a token that simply hasn't moved, with no signal why.
 *
 * **Names the decider once the server says who it is** (`decider`, {@link
 * import("./moveHoldDecider").deriveMoveHoldDecider} over the live `game_decision` map): "Ronin
 * threatens your start square". Until that decision lands (the prediction is instant, the server's
 * decision a round trip later) or when the decider is hidden from this client, the line is simply
 * absent. It never reads a possibly-hostile combatant's `base`/`current` fields, only its name.
 *
 * Unlike {@link import("./RejectionToast").RejectionToast} it carries no auto-dismiss timer — the
 * wait is a real in-flight state. {@link
 * import("./remoteEncounterController").RemoteEncounterController} clears the underlying
 * `pendingMove` the moment the authoritative broadcast for this move lands (applied or rejected).
 * With no checkpoint consumers registered in production, nothing halts and this never renders yet.
 * `key={pending.seq}` replays the enter animation on a repeat wait.
 */
import type { MoveHoldDecider } from "./moveHoldDecider";
import type { PendingMove } from "./remoteEncounterController";

export function MoveHoldBanner({ pending, name, decider }: { pending: PendingMove | null; name: string; decider: MoveHoldDecider | null }) {
  if (!pending) return null;
  return (
    <div key={pending.seq} className="combat-reactwait" role="status" aria-live="polite">
      <span className="combat-reactwait-spinner" aria-hidden="true" />
      <div className="combat-reactwait-text">
        <span className="combat-reactwait-eyebrow">OMNINET ⁄⁄ Move pending</span>
        <span className="combat-reactwait-reason">
          <b>{name}</b>&apos;s move awaits a reaction…
        </span>
        {decider && <MoveHoldDeciderLine decider={decider} />}
      </div>
    </div>
  );
}

/** The "who is deciding and why" line both hold banners share: "<b>Ronin</b> threatens your start square". */
export function MoveHoldDeciderLine({ decider }: { decider: MoveHoldDecider }) {
  return (
    <span className="combat-reactwait-queue combat-reactwait-decider">
      <b>{decider.deciderName}</b> {decider.why}
    </span>
  );
}
